import {
  getUploadedResourceKind,
  type UploadedResource,
} from "../media/uploadedResources"

export type VideoModelPrice = {
  /** 当前报价对应的生成分辨率。 */
  resolution: API.VideoResolution
  /** 当前价格档位包含的图片数量上限；不填表示不按图片数量分档，分档按上限升序配置。 */
  maxImageCount?: number
  /** 各生成时长（秒）对应的积点消耗，按时长升序配置。 */
  items: { duration: number; points: number }[]
}

export type VideoModelConfig = {
  /** 视频生成报价，分辨率与时长选项从此配置派生。 */
  pricing: VideoModelPrice[]
  /** 模型展示名称。 */
  label: string
  /** 是否在模型选项中显示 NEW 标记。 */
  isNew: boolean
  /** 复刻入口的卡片分组：standard 为常规模型，extended 为 Seedance 2.5 / Wan 3.0。 */
  /** 最终生成视频时允许传入的参考图片总数上限。 */
  maxImageCount: number
  /** 是否支持使用真人图片作为参考素材。 */
  supportsRealPerson: boolean
  /** 参考音频的数量上限。 */
  maxAudioCount: number
  /** 所有参考音频的合计时长上限，单位为秒；null 表示未配置模型专属时长限制。 */
  maxAudioTotalDuration: number | null
  /** 是否支持输入参考视频；不影响复刻流程中用于分析的原视频。 */
  supportsVideo: boolean
  /** 支持生成的视频画面比例。 */
  ratios: readonly API.VideoRatio[]
  /** 是否需要提示该模型对商品外观、包装文字的还原能力有限。 */
  limitedTextFidelity: boolean
  /** 模型选项中展示的能力说明。 */
  description: string
  /** 模型选项中展示的特点标签，如“真人参考”。 */
  tag: string
  /** 模型标签的样式标识，对应组件中的配色样式。 */
  tone: "mini" | "fast" | "standard" | "strong" | "h3" | "wan"
  /** 模型标识圆点的颜色。 */
  color: string
}

const VIDEO_RATIOS = ["21:9", "16:9", "4:3", "1:1", "3:4", "9:16"] as const
export const DEFAULT_VIDEO_CHANNEL =
  "ark_seedance" satisfies API.Task.CreateVideoChannel

// 新增模型时，在此添加能力和 pricing 报价配置，channel 类型由配置键名自动推导。
// 卡片、模型选项、草稿恢复及素材校验共用此配置；分辨率和时长从报价中派生。
// 用于分析的原视频与模型输入的参考视频分开处理。
export const VIDEO_MODELS = {
  ark_seedance_mini: {
    label: "Seedance 2.0 mini",
    isNew: false,
    maxImageCount: 9,
    supportsRealPerson: false,
    maxAudioCount: 3,
    maxAudioTotalDuration: null,
    supportsVideo: false,
    ratios: VIDEO_RATIOS,
    limitedTextFidelity: true,
    description:
      "适合批量生成简单商品视频，复杂动作、细节与文字还原较弱；最长 15 秒",
    tag: "轻量款",
    tone: "mini",
    color: "#94a3b8",
    pricing: [
      {
        resolution: "480p",
        items: [
          { duration: 4, points: 6 },
          { duration: 5, points: 7 },
          { duration: 6, points: 8 },
          { duration: 7, points: 10 },
          { duration: 8, points: 11 },
          { duration: 9, points: 12 },
          { duration: 10, points: 14 },
          { duration: 11, points: 15 },
          { duration: 12, points: 16 },
          { duration: 13, points: 18 },
          { duration: 14, points: 19 },
          { duration: 15, points: 20 },
        ],
      },
      {
        resolution: "720p",
        items: [
          { duration: 4, points: 12 },
          { duration: 5, points: 15 },
          { duration: 6, points: 18 },
          { duration: 7, points: 21 },
          { duration: 8, points: 24 },
          { duration: 9, points: 27 },
          { duration: 10, points: 30 },
          { duration: 11, points: 33 },
          { duration: 12, points: 36 },
          { duration: 13, points: 39 },
          { duration: 14, points: 42 },
          { duration: 15, points: 45 },
        ],
      },
    ],
  },
  ark_seedance_lite: {
    label: "Seedance 2.0 fast",
    isNew: false,
    maxImageCount: 9,
    supportsRealPerson: false,
    maxAudioCount: 3,
    maxAudioTotalDuration: null,
    supportsVideo: false,
    ratios: VIDEO_RATIOS,
    limitedTextFidelity: true,
    description: "适合日常带货视频，动作、细节与文字还原优于 mini；最长 15 秒",
    tag: "性价比高",
    tone: "fast",
    color: "#14a89e",
    pricing: [
      {
        resolution: "480p",
        items: [
          { duration: 4, points: 9 },
          { duration: 5, points: 11 },
          { duration: 6, points: 13 },
          { duration: 7, points: 15 },
          { duration: 8, points: 18 },
          { duration: 9, points: 20 },
          { duration: 10, points: 22 },
          { duration: 11, points: 24 },
          { duration: 12, points: 27 },
          { duration: 13, points: 29 },
          { duration: 14, points: 31 },
          { duration: 15, points: 34 },
        ],
      },
      {
        resolution: "720p",
        items: [
          { duration: 4, points: 19 },
          { duration: 5, points: 24 },
          { duration: 6, points: 29 },
          { duration: 7, points: 34 },
          { duration: 8, points: 38 },
          { duration: 9, points: 43 },
          { duration: 10, points: 48 },
          { duration: 11, points: 53 },
          { duration: 12, points: 58 },
          { duration: 13, points: 63 },
          { duration: 14, points: 68 },
          { duration: 15, points: 73 },
        ],
      },
    ],
  },
  ark_seedance: {
    label: "Seedance 2.0 标准",
    isNew: false,
    maxImageCount: 9,
    supportsRealPerson: false,
    maxAudioCount: 3,
    maxAudioTotalDuration: null,
    supportsVideo: false,
    ratios: VIDEO_RATIOS,
    limitedTextFidelity: false,
    description:
      "商品还原与人物一致性更好，适合高品质商品视频，成本较高；最长 15 秒",
    tag: "质量高",
    tone: "standard",
    color: "#8b5cf6",
    pricing: [
      {
        resolution: "480p",
        items: [
          { duration: 4, points: 11 },
          { duration: 5, points: 14 },
          { duration: 6, points: 16 },
          { duration: 7, points: 19 },
          { duration: 8, points: 22 },
          { duration: 9, points: 25 },
          { duration: 10, points: 28 },
          { duration: 11, points: 31 },
          { duration: 12, points: 34 },
          { duration: 13, points: 36 },
          { duration: 14, points: 39 },
          { duration: 15, points: 42 },
        ],
      },
      {
        resolution: "720p",
        items: [
          { duration: 4, points: 24 },
          { duration: 5, points: 30 },
          { duration: 6, points: 36 },
          { duration: 7, points: 42 },
          { duration: 8, points: 48 },
          { duration: 9, points: 54 },
          { duration: 10, points: 60 },
          { duration: 11, points: 66 },
          { duration: 12, points: 72 },
          { duration: 13, points: 78 },
          { duration: 14, points: 84 },
          { duration: 15, points: 91 },
        ],
      },
      {
        resolution: "1080p",
        items: [
          { duration: 4, points: 60 },
          { duration: 5, points: 75 },
          { duration: 6, points: 91 },
          { duration: 7, points: 106 },
          { duration: 8, points: 121 },
          { duration: 9, points: 136 },
          { duration: 10, points: 151 },
          { duration: 11, points: 167 },
          { duration: 12, points: 182 },
          { duration: 13, points: 197 },
          { duration: 14, points: 218 },
          { duration: 15, points: 230 },
        ],
      },
    ],
  },
  ark_seedance_2_5: {
    label: "Seedance 2.5",
    isNew: false,
    maxImageCount: 30,
    supportsRealPerson: false,
    maxAudioCount: 3,
    maxAudioTotalDuration: null,
    supportsVideo: false,
    ratios: VIDEO_RATIOS,
    limitedTextFidelity: false,
    description: "擅长复杂动作与多素材生成，适合高难度视频，成本高；最长 30 秒",
    tag: "综合能力强",
    tone: "strong",
    color: "#f59e0b",
    pricing: [
      {
        resolution: "480p",
        items: [
          { duration: 4, points: 15 },
          { duration: 5, points: 19 },
          { duration: 6, points: 23 },
          { duration: 7, points: 27 },
          { duration: 8, points: 30 },
          { duration: 9, points: 33 },
          { duration: 10, points: 35 },
          { duration: 11, points: 39 },
          { duration: 12, points: 42 },
          { duration: 13, points: 46 },
          { duration: 14, points: 50 },
          { duration: 15, points: 53 },
          { duration: 16, points: 56 },
          { duration: 17, points: 59 },
          { duration: 18, points: 62 },
          { duration: 19, points: 66 },
          { duration: 20, points: 70 },
          { duration: 21, points: 74 },
          { duration: 22, points: 78 },
          { duration: 23, points: 82 },
          { duration: 24, points: 86 },
          { duration: 25, points: 90 },
          { duration: 26, points: 93 },
          { duration: 27, points: 96 },
          { duration: 28, points: 100 },
          { duration: 29, points: 104 },
          { duration: 30, points: 108 },
        ],
      },
      {
        resolution: "720p",
        items: [
          { duration: 4, points: 31 },
          { duration: 5, points: 37 },
          { duration: 6, points: 45 },
          { duration: 7, points: 52 },
          { duration: 8, points: 60 },
          { duration: 9, points: 69 },
          { duration: 10, points: 78 },
          { duration: 11, points: 87 },
          { duration: 12, points: 96 },
          { duration: 13, points: 104 },
          { duration: 14, points: 110 },
          { duration: 15, points: 117 },
          { duration: 16, points: 123 },
          { duration: 17, points: 130 },
          { duration: 18, points: 137 },
          { duration: 19, points: 143 },
          { duration: 20, points: 151 },
          { duration: 21, points: 158 },
          { duration: 22, points: 164 },
          { duration: 23, points: 170 },
          { duration: 24, points: 176 },
          { duration: 25, points: 183 },
          { duration: 26, points: 190 },
          { duration: 27, points: 197 },
          { duration: 28, points: 204 },
          { duration: 29, points: 210 },
          { duration: 30, points: 218 },
        ],
      },
    ],
  },
  ali_bailian_wan_3: {
    label: "Wan 3.0",
    isNew: true,
    maxImageCount: 10,
    supportsRealPerson: true,
    maxAudioCount: 5,
    maxAudioTotalDuration: 15,
    supportsVideo: false,
    ratios: VIDEO_RATIOS.filter((ratio) => ratio !== "21:9"),
    limitedTextFidelity: false,
    description:
      "支持真人图片参考，人物形象还原更好；适合商品讲解、剧情带货视频；最长 30 秒",
    tag: "真人参考",
    tone: "wan",
    color: "#1677ff",
    pricing: [
      {
        resolution: "480p",
        items: [
          { duration: 4, points: 6 },
          { duration: 5, points: 8 },
          { duration: 6, points: 10 },
          { duration: 7, points: 11 },
          { duration: 8, points: 13 },
          { duration: 9, points: 15 },
          { duration: 10, points: 16 },
          { duration: 11, points: 17 },
          { duration: 12, points: 18 },
          { duration: 13, points: 20 },
          { duration: 14, points: 21 },
          { duration: 15, points: 23 },
          { duration: 16, points: 25 },
          { duration: 17, points: 27 },
          { duration: 18, points: 29 },
          { duration: 19, points: 30 },
          { duration: 20, points: 31 },
          { duration: 21, points: 33 },
          { duration: 22, points: 35 },
          { duration: 23, points: 37 },
          { duration: 24, points: 38 },
          { duration: 25, points: 40 },
          { duration: 26, points: 41 },
          { duration: 27, points: 42 },
          { duration: 28, points: 44 },
          { duration: 29, points: 45 },
          { duration: 30, points: 47 },
        ],
      },
      {
        resolution: "720p",
        items: [
          { duration: 4, points: 12 },
          { duration: 5, points: 16 },
          { duration: 6, points: 20 },
          { duration: 7, points: 22 },
          { duration: 8, points: 26 },
          { duration: 9, points: 30 },
          { duration: 10, points: 32 },
          { duration: 11, points: 34 },
          { duration: 12, points: 36 },
          { duration: 13, points: 40 },
          { duration: 14, points: 42 },
          { duration: 15, points: 46 },
          { duration: 16, points: 50 },
          { duration: 17, points: 54 },
          { duration: 18, points: 58 },
          { duration: 19, points: 60 },
          { duration: 20, points: 62 },
          { duration: 21, points: 66 },
          { duration: 22, points: 70 },
          { duration: 23, points: 74 },
          { duration: 24, points: 76 },
          { duration: 25, points: 80 },
          { duration: 26, points: 82 },
          { duration: 27, points: 84 },
          { duration: 28, points: 88 },
          { duration: 29, points: 90 },
          { duration: 30, points: 94 },
        ],
      },
      {
        resolution: "1080p",
        items: [
          { duration: 4, points: 24 },
          { duration: 5, points: 32 },
          { duration: 6, points: 40 },
          { duration: 7, points: 44 },
          { duration: 8, points: 52 },
          { duration: 9, points: 60 },
          { duration: 10, points: 64 },
          { duration: 11, points: 68 },
          { duration: 12, points: 72 },
          { duration: 13, points: 80 },
          { duration: 14, points: 84 },
          { duration: 15, points: 92 },
          { duration: 16, points: 100 },
          { duration: 17, points: 108 },
          { duration: 18, points: 116 },
          { duration: 19, points: 120 },
          { duration: 20, points: 124 },
          { duration: 21, points: 132 },
          { duration: 22, points: 140 },
          { duration: 23, points: 148 },
          { duration: 24, points: 152 },
          { duration: 25, points: 160 },
          { duration: 26, points: 164 },
          { duration: 27, points: 168 },
          { duration: 28, points: 176 },
          { duration: 29, points: 180 },
          { duration: 30, points: 188 },
        ],
      },
    ],
  },
  minimax_h3: {
    label: "MiniMax H3",
    isNew: false,
    maxImageCount: 9,
    supportsRealPerson: false,
    maxAudioCount: 3,
    maxAudioTotalDuration: null,
    supportsVideo: false,
    ratios: VIDEO_RATIOS,
    limitedTextFidelity: false,
    description:
      "成本最低，适合预算有限的商品展示与广告视频，复杂动作与细节还原较弱；最长 15 秒",
    tag: "最省钱",
    tone: "h3",
    color: "#ec4899",
    pricing: [
      {
        resolution: "768p",
        maxImageCount: 5,
        items: [
          { duration: 4, points: 12 },
          { duration: 5, points: 14 },
          { duration: 6, points: 16 },
          { duration: 7, points: 18 },
          { duration: 8, points: 20 },
          { duration: 9, points: 23 },
          { duration: 10, points: 26 },
          { duration: 11, points: 29 },
          { duration: 12, points: 31 },
          { duration: 13, points: 33 },
          { duration: 14, points: 35 },
          { duration: 15, points: 37 },
        ],
      },
      {
        resolution: "768p",
        maxImageCount: 6,
        items: [
          { duration: 4, points: 13 },
          { duration: 5, points: 15 },
          { duration: 6, points: 17 },
          { duration: 7, points: 19 },
          { duration: 8, points: 21 },
          { duration: 9, points: 24 },
          { duration: 10, points: 27 },
          { duration: 11, points: 30 },
          { duration: 12, points: 32 },
          { duration: 13, points: 34 },
          { duration: 14, points: 36 },
          { duration: 15, points: 38 },
        ],
      },
      {
        resolution: "768p",
        maxImageCount: 7,
        items: [
          { duration: 4, points: 14 },
          { duration: 5, points: 16 },
          { duration: 6, points: 18 },
          { duration: 7, points: 20 },
          { duration: 8, points: 22 },
          { duration: 9, points: 25 },
          { duration: 10, points: 28 },
          { duration: 11, points: 31 },
          { duration: 12, points: 33 },
          { duration: 13, points: 35 },
          { duration: 14, points: 37 },
          { duration: 15, points: 39 },
        ],
      },
      {
        resolution: "768p",
        maxImageCount: 8,
        items: [
          { duration: 4, points: 15 },
          { duration: 5, points: 17 },
          { duration: 6, points: 19 },
          { duration: 7, points: 21 },
          { duration: 8, points: 23 },
          { duration: 9, points: 26 },
          { duration: 10, points: 29 },
          { duration: 11, points: 32 },
          { duration: 12, points: 34 },
          { duration: 13, points: 36 },
          { duration: 14, points: 38 },
          { duration: 15, points: 40 },
        ],
      },
      {
        resolution: "768p",
        maxImageCount: 9,
        items: [
          { duration: 4, points: 16 },
          { duration: 5, points: 18 },
          { duration: 6, points: 20 },
          { duration: 7, points: 22 },
          { duration: 8, points: 24 },
          { duration: 9, points: 27 },
          { duration: 10, points: 30 },
          { duration: 11, points: 33 },
          { duration: 12, points: 35 },
          { duration: 13, points: 37 },
          { duration: 14, points: 39 },
          { duration: 15, points: 41 },
        ],
      },
      {
        resolution: "2K",
        maxImageCount: 5,
        items: [
          { duration: 4, points: 20 },
          { duration: 5, points: 22 },
          { duration: 6, points: 26 },
          { duration: 7, points: 32 },
          { duration: 8, points: 36 },
          { duration: 9, points: 38 },
          { duration: 10, points: 44 },
          { duration: 11, points: 48 },
          { duration: 12, points: 52 },
          { duration: 13, points: 58 },
          { duration: 14, points: 60 },
          { duration: 15, points: 64 },
        ],
      },
      {
        resolution: "2K",
        maxImageCount: 6,
        items: [
          { duration: 4, points: 21 },
          { duration: 5, points: 23 },
          { duration: 6, points: 27 },
          { duration: 7, points: 33 },
          { duration: 8, points: 37 },
          { duration: 9, points: 39 },
          { duration: 10, points: 45 },
          { duration: 11, points: 49 },
          { duration: 12, points: 53 },
          { duration: 13, points: 59 },
          { duration: 14, points: 61 },
          { duration: 15, points: 65 },
        ],
      },
      {
        resolution: "2K",
        maxImageCount: 7,
        items: [
          { duration: 4, points: 22 },
          { duration: 5, points: 24 },
          { duration: 6, points: 28 },
          { duration: 7, points: 34 },
          { duration: 8, points: 38 },
          { duration: 9, points: 40 },
          { duration: 10, points: 46 },
          { duration: 11, points: 50 },
          { duration: 12, points: 54 },
          { duration: 13, points: 60 },
          { duration: 14, points: 62 },
          { duration: 15, points: 66 },
        ],
      },
      {
        resolution: "2K",
        maxImageCount: 8,
        items: [
          { duration: 4, points: 23 },
          { duration: 5, points: 25 },
          { duration: 6, points: 29 },
          { duration: 7, points: 35 },
          { duration: 8, points: 39 },
          { duration: 9, points: 41 },
          { duration: 10, points: 47 },
          { duration: 11, points: 51 },
          { duration: 12, points: 55 },
          { duration: 13, points: 61 },
          { duration: 14, points: 63 },
          { duration: 15, points: 67 },
        ],
      },
      {
        resolution: "2K",
        maxImageCount: 9,
        items: [
          { duration: 4, points: 24 },
          { duration: 5, points: 26 },
          { duration: 6, points: 30 },
          { duration: 7, points: 36 },
          { duration: 8, points: 40 },
          { duration: 9, points: 42 },
          { duration: 10, points: 48 },
          { duration: 11, points: 52 },
          { duration: 12, points: 56 },
          { duration: 13, points: 62 },
          { duration: 14, points: 64 },
          { duration: 15, points: 68 },
        ],
      },
    ],
  },
} satisfies Record<string, VideoModelConfig>

export const VIDEO_CHANNEL_OPTIONS = (
  Object.keys(VIDEO_MODELS) as API.Task.CreateVideoChannel[]
).map((value) => Object.assign({}, VIDEO_MODELS[value], { value }))

export const getVideoModelConfig = (
  channel: API.Task.CreateVideoChannel,
): VideoModelConfig => {
  return VIDEO_MODELS[channel] ?? VIDEO_MODELS[DEFAULT_VIDEO_CHANNEL]
}

export const VIDEO_CHANNEL_RESOLUTIONS = VIDEO_CHANNEL_OPTIONS.reduce(
  (acc, { value, pricing }) => {
    acc[value] = Array.from(
      new Set(pricing.map(({ resolution }) => resolution)),
    )
    return acc
  },
  {} as Record<API.Task.CreateVideoChannel, API.VideoResolution[]>,
)

export const getVideoDurationOptions = (
  channel: API.Task.CreateVideoChannel,
): number[] => {
  return Array.from(
    new Set(
      getVideoModelConfig(channel).pricing.flatMap((item) =>
        item.items.map(({ duration }) => duration),
      ),
    ),
  ).sort((a, b) => a - b)
}

export type VideoModelDisabledReasons = Partial<
  Record<API.Task.CreateVideoChannel, string>
>

// 计算每个模型在当前素材 / 时长下不可选的原因，供模型选择器直接展示
export const getVideoModelDisabledReasons = (
  options: Parameters<typeof getVideoModelSelectionError>[1],
): VideoModelDisabledReasons => {
  const reasons: VideoModelDisabledReasons = {}
  for (const { value } of VIDEO_CHANNEL_OPTIONS) {
    const reason = getVideoModelSelectionError(value, options)
    if (reason) reasons[value] = reason
  }
  return reasons
}

export const getVideoModelSelectionError = (
  channel: API.Task.CreateVideoChannel,
  options: { duration?: number; allowImageOverflow?: boolean } & (
    | { resources: UploadedResource[] }
    | { referenceImageCount: number }
  ),
): string => {
  const config = getVideoModelConfig(channel)
  const { duration } = options
  const resources = "resources" in options ? options.resources : undefined
  const kinds = resources?.map(getUploadedResourceKind) ?? []
  const referenceImageCount =
    "referenceImageCount" in options
      ? options.referenceImageCount
      : kinds.filter((kind) => kind === "image").length

  if (!config.supportsVideo && kinds.includes("video")) {
    return `${config.label} 不支持输入参考视频`
  }
  if (
    !options.allowImageOverflow &&
    referenceImageCount > config.maxImageCount
  ) {
    return `${config.label} 最多支持 ${config.maxImageCount} 张参考图，当前有 ${referenceImageCount} 张`
  }
  const audioCount = kinds.filter((kind) => kind === "audio").length
  if (audioCount > config.maxAudioCount) {
    return `${config.label} 最多支持 ${config.maxAudioCount} 个参考音频，当前有 ${audioCount} 个`
  }
  const durations = getVideoDurationOptions(channel)
  if (
    typeof duration === "number" &&
    (!Number.isFinite(duration) ||
      duration < durations[0] ||
      duration > durations.at(-1)!)
  ) {
    return `${config.label} 仅支持 ${durations[0]}-${durations.at(-1)} 秒视频`
  }
  return ""
}

const readAudioDuration = (url: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const audio = document.createElement("audio")
    const cleanup = () => {
      window.clearTimeout(timer)
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata)
      audio.removeEventListener("error", fail)
      audio.removeAttribute("src")
      audio.load()
    }
    const fail = () => {
      cleanup()
      reject(new Error("无法读取参考音频时长，请重新上传后再试"))
    }
    const timer = window.setTimeout(fail, 10000)
    audio.preload = "metadata"
    const handleLoadedMetadata = () => {
      const duration = audio.duration
      if (!Number.isFinite(duration) || duration <= 0) {
        fail()
        return
      }
      cleanup()
      resolve(duration)
    }
    audio.addEventListener("loadedmetadata", handleLoadedMetadata, {
      once: true,
    })
    audio.addEventListener("error", fail, { once: true })
    audio.src = url
  })
}

// Reuse successful metadata reads when validating the same library asset again.
const AUDIO_DURATIONS = new Map<string, number>()

export const getVideoReferenceAudioError = async (
  channel: API.Task.CreateVideoChannel,
  urls: string[],
): Promise<string> => {
  const config = getVideoModelConfig(channel)
  if (urls.length > config.maxAudioCount) {
    return `参考音频最多 ${config.maxAudioCount} 个`
  }
  if (config.maxAudioTotalDuration === null || !urls.length) return ""
  try {
    const durations = await Promise.all(
      urls.map(async (url) => {
        const cached = AUDIO_DURATIONS.get(url)
        if (cached !== undefined) return cached
        const duration = await readAudioDuration(url)
        if (!url.startsWith("blob:")) {
          if (AUDIO_DURATIONS.size >= 100) AUDIO_DURATIONS.clear()
          AUDIO_DURATIONS.set(url, duration)
        }
        return duration
      }),
    )
    return durations.reduce((total, duration) => total + duration, 0) >
      config.maxAudioTotalDuration
      ? `参考音频总时长不能超过${config.maxAudioTotalDuration}秒，请裁剪后重新上传`
      : ""
  } catch (error) {
    return error instanceof Error ? error.message : "读取参考音频时长失败"
  }
}

export const getVideoReferenceRequirements = (
  channel: API.Task.CreateVideoChannel,
): string => {
  const config = getVideoModelConfig(channel)
  return [
    `图片最多 ${config.maxImageCount} 张`,
    `音频最多 ${config.maxAudioCount} 个`,
    config.maxAudioTotalDuration !== null
      ? `音频总时长不超过 ${config.maxAudioTotalDuration} 秒`
      : "",
    !config.supportsVideo ? "不支持参考视频" : "",
    config.supportsRealPerson ? "支持真人图片参考" : "请勿上传真人肖像素材",
  ]
    .filter(Boolean)
    .join("，")
}
